import { HandCoins } from "lucide-react";
import { linePath, smoothPoints, type Pt } from "features/onboarding/art/chart";
import {
  Art,
  ArtSwitch,
  Chip,
  Head,
  Line,
  Status,
  Toast,
  Window,
} from "features/onboarding/art/live";
import {
  TXT,
  fade,
  font,
  pop,
  r2,
  reach,
  ride,
  ring,
  rise,
  say,
  soft,
  tone,
  useArtId,
  type Box,
  type Scene,
} from "features/onboarding/art/kit";
import useArtLabels from "features/onboarding/art/labels";

// Ported from cornix.io (feature/art/dca-bots.tsx): the bot buys more as the
// price dips, lowering its average entry, and closes at take-profit.

// The price comes down to entry 1 (the first buy), keeps dipping through the
// safety orders, then rebounds to take-profit.
const KEYS: Pt[] = [
  [0, 0.04],
  [0.3, 0.33],
  [0.5, 0.62],
  [0.69, 0.92],
  [0.84, 0.62],
  [1.0, 0.3],
];
const BASE = 0.16;
const SO = [0.36, 0.54, 0.82];
const TOP = 3560;
const RANGE = 420;
const price = (v: number) => TOP - v * RANGE;
// Entries scale up (1×, 1×, 2×, 3×), so each fill pulls the average down
// further; take-profit sits 1.5% above the average.
const SIZE = [1, 1, 2, 3];
const AVG = [BASE, ...SO].map((_, i, all) => {
  const n = SIZE.slice(0, i + 1);
  return (
    all.slice(0, i + 1).reduce((sum, v, j) => sum + v * n[j], 0) /
    n.reduce((a, b) => a + b, 0)
  );
});
const TP = AVG.map((v) => (TOP - price(v) * 1.015) / RANGE);
const usd = (v: number) => `$${Math.round(price(v)).toLocaleString("en-US")}`;
const num = (v: number) => Math.round(price(v)).toLocaleString("en-US");

type Geo = {
  name: string;
  w: number;
  h: number;
  win: Box;
  chart: Box;
  band: Box;
  tagX: number;
  stats: Box[];
  big: boolean;
  toast: { x: number; y: number; w: number };
};

const WIDE: Geo = {
  name: "w",
  w: 640,
  h: 440,
  win: { x: 22, y: 20, w: 536, h: 392 },
  chart: { x: 42, y: 72, w: 366, h: 320 },
  band: { x: 100, y: 112, w: 232, h: 254 },
  tagX: 338,
  stats: [
    { x: 420, y: 72, w: 122, h: 82 },
    { x: 420, y: 166, w: 122, h: 82 },
    { x: 420, y: 260, w: 122, h: 82 },
  ],
  big: true,
  toast: { x: 420, y: 364, w: 214 },
};

const TALL: Geo = {
  name: "t",
  w: 360,
  h: 482,
  win: { x: 8, y: 8, w: 344, h: 392 },
  chart: { x: 22, y: 52, w: 316, h: 246 },
  band: { x: 84, y: 88, w: 166, h: 198 },
  tagX: 256,
  stats: [
    { x: 22, y: 314, w: 100, h: 68 },
    { x: 130, y: 314, w: 100, h: 68 },
    { x: 238, y: 314, w: 100, h: 68 },
  ],
  big: false,
  toast: { x: 71, y: 414, w: 218 },
};

export function DcaBotsArt({ play }: { play?: boolean }) {
  return (
    <ArtSwitch
      wide={<DcaBots g={WIDE} play={play} />}
      narrow={<DcaBots g={TALL} play={play} />}
    />
  );
}

function DcaBots({ g, play }: { g: Geo; play?: boolean }) {
  const L = useArtLabels();
  const id = useArtId(`dca-${g.name}`);
  const { band } = g;
  const Y = (v: number) => r2(band.y + v * band.h);
  // The deal closes at take-profit, so the price line ends exactly there.
  const full = smoothPoints(
    KEYS.map(([u, v]): Pt => [band.x + u * band.w, Y(v)]),
    10
  );
  const tpY = Y(TP[3]);
  const low = full.reduce((b, p, i) => (p[1] > full[b][1] ? i : b), 0);
  const end = full.findIndex((p, i) => i > low && p[1] <= tpY);
  const [ax, ay] = full[end - 1];
  const [bx, by] = full[end];
  const k = (tpY - ay) / (by - ay);
  const pts: Pt[] = [...full.slice(0, end), [r2(ax + (bx - ax) * k), tpY]];
  // Every entry, the first included, fills where the price comes down to it.
  const LEVELS = [BASE, ...SO];
  const fills = LEVELS.map((v) => reach(pts, Y(v), "down")).filter(
    (c): c is NonNullable<typeof c> => c !== null
  );
  const tp = pts[pts.length - 1];
  const x1 = band.x + band.w;

  const START = 0.5;
  const DRAW = 5.6;
  const at = (f: number) => START + f * DRAW;
  const GLIDE = 0.7;

  const scene: Scene = (tl, $) => {
    // A clean start: nothing filled yet, no average or take-profit to show.
    tl.set([...$("avg"), ...$("tp")], { opacity: 0 }, 0);
    tl.set($("avg"), { y: Y(AVG[0]) }, 0);
    tl.set($("tp"), { y: Y(TP[0]) }, 0);
    say(tl, $("filled")[0], "0/4", 0);
    say(tl, $("avg-stat")[0], "—", 0);
    say(tl, $("tp-stat")[0], "—", 0);
    say(tl, $("avg-text")[0], num(AVG[0]), 0);
    say(tl, $("tp-text")[0], num(TP[0]), 0);

    ride(tl, $("price"), $("head")[0], pts, START, DRAW);
    fills.forEach((c, i) => {
      const t = at(c.f);
      pop(tl, $(`entry${i}`), t);
      ring(tl, $(`ring${i}`), t, 6, 22);
      say(tl, $("filled")[0], `${i + 1}/4`, t + 0.1);
      if (i === 0) {
        // The first buy sets the average and the take-profit above it.
        tl.to(
          [...$("avg"), ...$("tp")],
          { opacity: 1, duration: 0.5 },
          t + 0.1
        );
      } else {
        // Later buys pull both lines down together, gliding.
        tl.to(
          $("avg"),
          { y: Y(AVG[i]), duration: GLIDE, ease: "power2.inOut" },
          t + 0.1
        );
        tl.to(
          $("tp"),
          { y: Y(TP[i]), duration: GLIDE, ease: "power2.inOut" },
          t + 0.1
        );
        say(tl, $("avg-text")[0], num(AVG[i]), t + 0.1 + GLIDE / 2);
        say(tl, $("tp-text")[0], num(TP[i]), t + 0.1 + GLIDE / 2);
      }
      say(tl, $("avg-stat")[0], usd(AVG[i]), t + 0.1 + GLIDE / 2);
      say(tl, $("tp-stat")[0], usd(TP[i]), t + 0.1 + GLIDE / 2);
    });
    const hit = START + DRAW;
    ring(tl, $("burst*"), hit, 6, 40);
    rise(tl, $("win"), hit, 8, 0.6);
    rise(tl, $("toast"), hit + 0.4, 12, 0.7);
    const rest = hit + 1.2;

    const out = rest + 3.6;
    fade(
      tl,
      [
        ...$("price"),
        ...$("head"),
        ...$("entry*"),
        ...$("win"),
        ...$("toast"),
        ...$("avg"),
        ...$("tp"),
      ],
      out
    );
    // With the chart cleared, the figures reset too, so the next loop starts
    // from nothing filled.
    say(tl, $("filled")[0], "0/4", out + 0.5);
    say(tl, $("avg-stat")[0], "—", out + 0.5);
    say(tl, $("tp-stat")[0], "—", out + 0.5);
    tl.to({}, { duration: 0.6 }, out + 0.5);
    return rest;
  };

  // Right-aligned with the plot, between entries 1 and 2: clear of every line.
  const winText = g.big ? `${L.takeProfit} ✓` : "TP ✓";
  const winW = Math.round(winText.length * 7.2 + 22);
  const valueStyle = font(g.big ? 18 : 15, 600);
  const stats = [
    { label: L.filled, k: "filled", value: "4/4", t: undefined },
    {
      label: L.averageEntry,
      k: "avg-stat",
      value: usd(AVG[3]),
      t: "accent" as const,
    },
    { label: L.takeProfit, k: "tp-stat", value: usd(TP[3]), t: "up" as const },
  ];
  const tagW = g.chart.x + g.chart.w - 10 - g.tagX;
  const tag = (text: string, k: string, t: "up" | "accent", label: string) => (
    <>
      <rect x={g.tagX} y={-10} width={tagW} height={20} rx={6} fill={soft(t)} />
      <text
        x={g.tagX + tagW / 2}
        y={4}
        textAnchor="middle"
        fill={tone(t)}
        className={TXT.num}
        style={font(10.5, 600)}
      >
        <tspan style={font(9.5, 500)}>{label} </tspan>
        <tspan data-k={k}>{text}</tspan>
      </text>
    </>
  );

  return (
    <Art id={id} w={g.w} h={g.h} scene={scene} play={play}>
      <Window
        id={id}
        x={g.win.x}
        y={g.win.y}
        w={g.win.w}
        h={g.win.h}
        title={`${L.dcaBot} · ETH/USDT`}
      >
        {/* Entry levels; their labels have their own lane left of the plot,
            so no moving line ever crosses them. */}
        <g data-in="0.15">
          {LEVELS.map((v, i) => (
            <g key={v}>
              <line
                x1={band.x}
                x2={x1}
                y1={Y(v)}
                y2={Y(v)}
                stroke="var(--viz-rule)"
                strokeDasharray="3 5"
              />
              <text
                x={band.x - 10}
                y={Y(v) + 3.5}
                textAnchor="end"
                className={TXT.label}
              >
                {L.entryN(i + 1)}
              </text>
            </g>
          ))}
        </g>
        <g data-k="tp" transform={`translate(0 ${Y(TP[3])})`}>
          <line
            x1={band.x}
            x2={x1}
            stroke={tone("up")}
            strokeWidth={1.3}
            strokeDasharray="6 4"
          />
          {tag(num(TP[3]), "tp-text", "up", "TP")}
        </g>
        <g data-k="avg" transform={`translate(0 ${Y(AVG[3])})`}>
          <line x1={band.x} x2={x1} stroke={tone("accent")} strokeWidth={1.5} />
          {tag(num(AVG[3]), "avg-text", "accent", "Ø")}
        </g>

        <Line k="price" d={linePath(pts)} w={2.2} />
        {fills.map((c, i) => (
          <g key={i}>
            <circle
              data-k={`ring${i}`}
              cx={c.x}
              cy={Y(LEVELS[i])}
              r={6}
              fill="none"
              stroke={tone("accent")}
              strokeWidth={2}
              opacity={0}
            />
            <circle
              data-k={`entry${i}`}
              cx={c.x}
              cy={Y(LEVELS[i])}
              r={6.5}
              fill={tone("accent")}
              stroke="var(--viz-card)"
              strokeWidth={2}
            />
          </g>
        ))}
        <circle
          data-k="burst0"
          cx={tp[0]}
          cy={tp[1]}
          r={6}
          fill="none"
          stroke={tone("up")}
          strokeWidth={2}
          opacity={0}
        />
        <circle
          data-k="burst1"
          cx={tp[0]}
          cy={tp[1]}
          r={6}
          fill="none"
          stroke={tone("up")}
          strokeWidth={1.5}
          opacity={0}
        />
        <Chip
          k="win"
          x={x1 - winW / 2}
          y={Y((BASE + SO[0]) / 2)}
          text={winText}
          w={winW}
          t="up"
          solid
        />
        <Head k="head" x={tp[0]} y={tp[1]} />

        {g.stats.map((b, i) => {
          const s = stats[i];
          return (
            <g key={s.k} data-in={0.2 + i * 0.08}>
              <rect
                x={b.x}
                y={b.y}
                width={b.w}
                height={b.h}
                rx={12}
                fill="none"
                stroke="var(--viz-rule)"
              />
              <text x={b.x + 12} y={b.y + 22} className={TXT.label}>
                {s.label}
              </text>
              <text
                data-k={s.k}
                x={b.x + 12}
                y={b.y + (g.big ? 56 : 48)}
                fill={s.t ? tone(s.t) : undefined}
                className={s.t ? TXT.num : `${TXT.ink} ${TXT.num}`}
                style={valueStyle}
              >
                {s.value}
              </text>
            </g>
          );
        })}
        <Status x={g.win.x + g.win.w - 18} y={g.win.y + 23} text={L.active} />
      </Window>
      <Toast
        id={id}
        k="toast"
        x={g.toast.x}
        y={g.toast.y}
        w={g.toast.w}
        icon={HandCoins}
        title={`${L.positionClosed} · +1.5%`}
        meta={`4/4 · ${L.averageEntry} ↓`}
        t="up"
      />
    </Art>
  );
}
